/**
 * Flight Setup Component
 * 
 * Horizontal stepper for Planned Flight flow
 * Shows progress through Flight Setup and Review & Launch steps
 */

import { FC } from 'react';
import { cn } from '@/lib/utils';

export type FlightStep = 1 | 2;

interface FlightSetupProps {
    currentStep: FlightStep;
    onStepClick?: (step: FlightStep) => void;
}

interface StepItemProps {
    stepNumber: FlightStep;
    label: string;
    isActive: boolean;
    isCompleted: boolean;
    onClick?: () => void;
}

const StepItem: FC<StepItemProps> = ({ 
    stepNumber, 
    label, 
    isActive, 
    isCompleted,
    onClick 
}) => (
    <button
        type="button"
        onClick={onClick}
        disabled={!onClick}
        className={cn(
            "flex items-center gap-3 transition-colors",
            onClick && "cursor-pointer hover:opacity-80",
            !onClick && "cursor-default"
        )}
    >
        <div
            className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all",
                isActive && "bg-transparent border-2 border-cyan-400 text-cyan-400",
                isCompleted && "bg-cyan-400 border-2 border-cyan-400 text-slate-900",
                !isActive && !isCompleted && "bg-transparent border-2 border-slate-600 text-slate-400"
            )}
        >
            {stepNumber}
        </div>
        <span
            className={cn(
                "text-sm font-medium transition-colors",
                (isActive || isCompleted) && "text-white",
                !isActive && !isCompleted && "text-slate-400"
            )}
        >
            {label}
        </span>
    </button>
);

const StepConnector: FC<{ isCompleted: boolean }> = ({ isCompleted }) => (
    <div
        className={cn(
            "w-12 h-0.5 mx-2 transition-colors",
            isCompleted ? "bg-cyan-400" : "bg-slate-600"
        )}
    />
);

export const FlightSetup: FC<FlightSetupProps> = ({ 
    currentStep, 
    onStepClick 
}) => {
    const steps: { step: FlightStep; label: string }[] = [
        { step: 1, label: 'Flight Setup' },
        { step: 2, label: 'Review & Launch' },
    ];

    return (
        <div className="flex items-center mb-8">
            {steps.map((item, index) => (
                <div key={item.step} className="flex items-center">
                    <StepItem
                        stepNumber={item.step}
                        label={item.label}
                        isActive={currentStep === item.step}
                        isCompleted={currentStep > item.step}
                        onClick={
                            onStepClick && currentStep > item.step
                                ? () => onStepClick(item.step)
                                : undefined
                        }
                    />
                    {index < steps.length - 1 && (
                        <StepConnector isCompleted={currentStep > item.step} />
                    )}
                </div>
            ))}
        </div>
    );
};
